import React, { useCallback, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowRight } from "@fortawesome/free-solid-svg-icons";

// Common components
import Popup from "../Popup/Popup";
import AppTable from "../AppTable/AppTable";
import ExportCsvBtn from "../AppSpecificConpnents/ExportCsvBtn";
import FilterDropdown from "../AppSpecificConpnents/PartsTable/FilterDropdown";

// Assets
import Images from "../../assests/imgs/Imges";
import { formatVersionHistoryIdDisplay } from "./formatVersionHistoryIdDisplay";
import { mergeAuditTrailRowVersionContentDeep } from "../../pages/AuditTrail/utils/auditTrailProcedureIdentity.js";
import { normalizeVersionIdRef } from "../../util/versionIdRefUtils";
import "./VersionHistoryPopup.css";

/** Audit / preview flows: pick another version to compare (shared title + header color). */
export const VERSION_HISTORY_COMPARISON_SELECT_TITLE =
  "Select Version for Comparison";
export const VERSION_HISTORY_COMPARISON_HEADER_STYLE = { color: "#0D6EFD" };

function versionHistoryActionTimestamp(row) {
  if (!row || typeof row !== "object") return 0;
  const raw =
    row.audit_date ||
    row.audit_created_at ||
    row.version_created_at ||
    row.created_at ||
    row.version_updated_at;
  if (raw == null || raw === "") return 0;
  const n = new Date(raw).getTime();
  return Number.isNaN(n) ? 0 : n;
}

/** Latest audit / action first (reverse chronological). */
function sortVersionHistoryNewestFirst(rows) {
  if (!Array.isArray(rows)) return [];
  return [...rows].sort(
    (a, b) => versionHistoryActionTimestamp(b) - versionHistoryActionTimestamp(a),
  );
}

/** Client-side sort for AppTable column keys (ASC / DESC / DEF). */
function sortVersionHistoryRows(
  rows,
  sortKey,
  direction,
  breadcrumbText,
  revisionTreeNodes,
) {
  if (
    !Array.isArray(rows) ||
    !sortKey ||
    direction == null ||
    direction === "" ||
    direction === "DEF"
  ) {
    return rows;
  }
  const mult = direction === "ASC" ? 1 : -1;
  const locale = (a, b) =>
    mult *
    String(a ?? "").localeCompare(String(b ?? ""), undefined, {
      numeric: true,
      sensitivity: "base",
    });

  return [...rows].sort((ra, rb) => {
    switch (sortKey) {
      case "audit_date": {
        const ta = versionHistoryActionTimestamp(ra);
        const tb = versionHistoryActionTimestamp(rb);
        return mult * (ta - tb);
      }
      case "version_id": {
        const pathA = formatVersionHistoryIdDisplay(
          ra,
          breadcrumbText,
          revisionTreeNodes,
        );
        const pathB = formatVersionHistoryIdDisplay(
          rb,
          breadcrumbText,
          revisionTreeNodes,
        );
        return locale(pathA, pathB);
      }
      case "action":
        return locale(ra.action, rb.action);
      case "modified_by": {
        const ua = `${ra.first_name || ""} ${ra.last_name || ""}`.trim();
        const ub = `${rb.first_name || ""} ${rb.last_name || ""}`.trim();
        return locale(ua, ub);
      }
      case "comment":
        return locale(ra.comment, rb.comment);
      default:
        return 0;
    }
  });
}

/** Filterable column `field` values from VersionHistoryPopup `columnsData` (must match AppTable `search_type`). */
const VERSION_HISTORY_FILTER_FIELDS = [
  "audit_date",
  "version_id",
  "action",
  "modified_by",
  "comment",
];

const VERSION_HISTORY_POPUP_APP_TABLE_NOOP = () => {};

/**
 * Stable `pagination` reference for {@link AppTable}. The default prop is a new object each render,
 * which retriggers the filter effect and can cancel the search debounce before it fires.
 */
const VERSION_HISTORY_POPUP_APP_TABLE_PAGINATION = {
  limit: [100000, VERSION_HISTORY_POPUP_APP_TABLE_NOOP],
  offset: [0, VERSION_HISTORY_POPUP_APP_TABLE_NOOP],
  pagination: {},
  paginationPosition: {},
  paginationShadow: false,
  maxVsbleButtons: 10,
  showRecentItems: false,
  disabled: false,
};

/** Stable columns config — avoids needless AppTable sync when parent re-renders. */
const VERSION_HISTORY_POPUP_COLUMNS_DATA = [
  {
    headName: "Date",
    field: "audit_date",
    sort: true,
    filter: true,
    width: "15%",
  },
  {
    headName: "Version ID",
    field: "version_id",
    sort: true,
    filter: true,
    width: "36%",
  },
  {
    headName: "Activity",
    field: "action",
    sort: true,
    filter: true,
    width: "15%",
  },
  {
    headName: "Modified by",
    field: "modified_by",
    sort: true,
    filter: true,
    width: "15%",
  },
  {
    headName: "Comment",
    field: "comment",
    sort: true,
    filter: true,
    width: "20%",
  },
  { headName: "Action", field: "action_button", width: "5%" },
];

/**
 * AppTable calls onFilterSortHandler with a query string containing `search` and (when searching)
 * `search_type` — comma-separated column field names for "Select Columns". Client-side filtering must honor both.
 */
function parseTableSearchPayload(queryString) {
  if (!queryString || typeof queryString !== "string") {
    return { search: "", fields: null };
  }
  try {
    const params = new URLSearchParams(queryString);
    const search = params.get("search") ?? "";
    const rawType = params.get("search_type");
    const fields =
      rawType != null && String(rawType).trim() !== ""
        ? String(rawType)
            .split(",")
            .map((s) => s.trim())
            .filter(Boolean)
        : null;
    return { search, fields };
  } catch {
    return { search: "", fields: null };
  }
}

function versionHistoryFullSearchExtras(row) {
  if (!row || typeof row !== "object") return "";
  const vc = row.version_content?.breadcrumb_text;
  const pvc = row.parent_version_data?.version_content?.breadcrumb_text;
  return [vc, pvc]
    .filter((x) => typeof x === "string" && x.trim())
    .join(" ");
}

/** Text per logical column for substring search (aligned with AppTable `search_type` fields). */
function versionHistoryRowSearchHaystack(row, field, ctx) {
  if (!row || typeof row !== "object") return "";
  switch (field) {
    case "audit_date": {
      const parts = [];
      if (typeof ctx.formatDate === "function" && row.audit_date) {
        parts.push(ctx.formatDate(row.audit_date));
      }
      parts.push(
        row.audit_date,
        row.audit_created_at,
        row.version_created_at,
        row.created_at,
        row.version_updated_at,
      );
      return parts.filter((x) => x != null && String(x).trim() !== "").join(" ");
    }
    case "version_id":
      return [
        row.version_id,
        formatVersionHistoryIdDisplay(row, ctx.breadcrumbText, ctx.revisionTreeNodes),
      ]
        .filter((x) => x != null && String(x).trim() !== "")
        .join(" ");
    case "action":
      return row.action != null ? String(row.action) : "";
    case "modified_by":
      return `${row.first_name || ""} ${row.last_name || ""}`.trim();
    case "comment":
      return row.comment != null ? String(row.comment) : "";
    default:
      return "";
  }
}

/** VC keys audits may use for the same logical node (CREATE_* often uses tree_node_id; UPDATE_* may use *_*_tree_node_id). */
function collectVersionContentKeysForNodeHistory(nodeIdField) {
  return [
    ...new Set(
      [
        nodeIdField,
        "tree_node_id",
        "section_tree_node_id",
        "section_id_ref",
        "step_tree_node_id",
        "sub_step_tree_node_id",
        "substep_tree_node_id",
        "preview_tree_node_id",
        "attachment_tree_node_id",
        "canvas_tree_node_id",
        "canva_tree_node_id",
        "video_tree_node_id",
        "rich_text_tree_node_id",
        "rich_text_node_id",
        "element_id",
        "procedure_tree_ref",
        "procedure_tree_id",
        "attachment_tree_node_ref",
        "attachment_id",
      ].filter(Boolean),
    ),
  ];
}

function normalizeNodeHistoryMatchIdSet(rawIds) {
  const set = new Set();
  const list = Array.isArray(rawIds) ? rawIds : [];
  for (const raw of list) {
    if (raw == null || raw === "") continue;
    const s = String(raw).trim();
    if (s) set.add(s);
  }
  return set;
}

/**
 * Rows returned from version/get-all with parent tracing: walk `parent_version` from the
 * current node's anchor `version_id` so Node History shows the full chain even when VC only
 * has `preview_tree_node_id` for the revision where the leaf was pasted (copies diverge).
 */
function computeVersionAncestorIdSet(anchorVersionId, rows) {
  if (!anchorVersionId || !Array.isArray(rows) || rows.length === 0)
    return null;
  const parents = new Map();
  for (const r of rows) {
    const vid = r?.version_id;
    if (vid == null || String(vid).trim() === "") continue;
    const key = String(vid).trim();
    const pv = r?.parent_version;
    parents.set(
      key,
      pv != null && String(pv).trim() !== "" ? String(pv).trim() : null,
    );
  }
  const out = new Set();
  let cur = String(anchorVersionId).trim();
  const visited = new Set();
  let depth = 0;
  while (cur && depth < 500) {
    if (visited.has(cur)) break;
    visited.add(cur);
    out.add(cur);
    const next = parents.get(cur);
    if (!next || next === cur) break;
    cur = next;
    depth += 1;
  }
  return out;
}

/** API rows sometimes stringify version_content; nested envelopes use inner version_content. */
function parseVersionContentMaybe(raw) {
  if (raw == null) return {};
  if (typeof raw === "object" && !Array.isArray(raw)) return raw;
  if (typeof raw === "string") {
    const t = raw.trim();
    if (!t) return {};
    try {
      const o = JSON.parse(t);
      return o && typeof o === "object" && !Array.isArray(o) ? o : {};
    } catch {
      return {};
    }
  }
  return {};
}

function normalizeAuditRowVersionContentShapes(row) {
  if (!row || typeof row !== "object") return row;
  const parent = row.parent_version_data;
  return {
    ...row,
    version_content: parseVersionContentMaybe(row.version_content),
    parent_version_data:
      parent && typeof parent === "object"
        ? {
            ...parent,
            version_content: parseVersionContentMaybe(parent.version_content),
          }
        : parent,
  };
}

function rowMatchesNodeHistoryVersionContent(row, idSet, nodeIdField) {
  if (!row || idSet.size === 0) return false;

  const normalized = normalizeAuditRowVersionContentShapes(row);
  const mergedFlat =
    mergeAuditTrailRowVersionContentDeep(normalized) ||
    normalized.version_content ||
    {};

  const keys = collectVersionContentKeysForNodeHistory(nodeIdField);
  for (const key of keys) {
    const val = mergedFlat[key];
    if (val == null || val === "") continue;
    if (idSet.has(String(val).trim())) return true;
  }

  const rootKeys = [
    "tree_node_id",
    "preview_tree_node_id",
    "section_tree_node_id",
    "procedure_tree_ref",
    "procedure_tree_id",
  ];
  for (const key of rootKeys) {
    const val = row[key];
    if (val != null && val !== "" && idSet.has(String(val).trim())) return true;
  }

  return false;
}

/** Sparse audits: only action_to_show mentions "Save Section N" with no ids in VC. */
function rowMatchesSectionAuditOrdinal(row, sectionOrdinal) {
  if (sectionOrdinal == null || sectionOrdinal === "") return false;
  const n = Number(sectionOrdinal);
  if (!Number.isFinite(n) || n < 1) return false;
  const normalized = normalizeAuditRowVersionContentShapes(row);
  const mergedFlat =
    mergeAuditTrailRowVersionContentDeep(normalized) ||
    normalized.version_content ||
    {};
  const text = [
    row?.action,
    row?.comment,
    mergedFlat?.action_to_show,
  ]
    .filter((x) => typeof x === "string" && x.trim())
    .join("\n");
  if (!text) return false;
  const re = new RegExp(
    `\\b(?:Save|Edit|Update|Add|Delete|Create|Remove|Move|Copy)\\s+Section\\s+${n}\\b`,
    "i",
  );
  return re.test(text);
}

/** Sparse step audits: action lines like "Save Step N …" without ids in VC (global procedure step index). */
function rowMatchesStepAuditOrdinal(row, stepOrdinal) {
  if (stepOrdinal == null || stepOrdinal === "") return false;
  const n = Number(stepOrdinal);
  if (!Number.isFinite(n) || n < 1) return false;
  const normalized = normalizeAuditRowVersionContentShapes(row);
  const mergedFlat =
    mergeAuditTrailRowVersionContentDeep(normalized) ||
    normalized.version_content ||
    {};
  const text = [
    row?.action,
    row?.comment,
    mergedFlat?.action_to_show,
  ]
    .filter((x) => typeof x === "string" && x.trim())
    .join("\n");
  if (!text) return false;
  const re = new RegExp(
    `\\b(?:Save|Edit|Update|Add|Delete|Create|Remove|Move|Copy)\\s+Step\\s+${n}\\b`,
    "i",
  );
  return re.test(text);
}

/** Sparse substep audits: "Save Substep 3A …" (global step index + letter) — avoids matching plain Step N rows on view-substep. */
function rowMatchesSubstepAuditOrdinal(row, stepOrdinal, letterSuffix) {
  if (stepOrdinal == null || stepOrdinal === "") return false;
  const n = Number(stepOrdinal);
  if (!Number.isFinite(n) || n < 1) return false;
  const suf =
    typeof letterSuffix === "string" ? letterSuffix.trim() : "";
  if (!suf) return false;
  const escapedSuffix = suf.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const normalized = normalizeAuditRowVersionContentShapes(row);
  const mergedFlat =
    mergeAuditTrailRowVersionContentDeep(normalized) ||
    normalized.version_content ||
    {};
  const text = [
    row?.action,
    row?.comment,
    mergedFlat?.action_to_show,
  ]
    .filter((x) => typeof x === "string" && x.trim())
    .join("\n");
  if (!text) return false;
  const re = new RegExp(
    `\\b(?:Save|Edit|Update|Add|Delete|Create|Remove|Move|Copy)\\s+Substep\\s+${n}${escapedSuffix}\\b`,
    "i",
  );
  return re.test(text);
}

/** Sparse leaf audits on view-childnode: "Save Attachment 3 …", "Update Rich Text 2 …" (global media ordinal). */
function rowMatchesLeafMediaAuditOrdinal(row, mediaOrdinal, phraseRegexFragment) {
  if (mediaOrdinal == null || mediaOrdinal === "") return false;
  const n = Number(mediaOrdinal);
  if (!Number.isFinite(n) || n < 1) return false;
  const frag =
    typeof phraseRegexFragment === "string"
      ? phraseRegexFragment.trim()
      : "";
  if (!frag) return false;
  const normalized = normalizeAuditRowVersionContentShapes(row);
  const mergedFlat =
    mergeAuditTrailRowVersionContentDeep(normalized) ||
    normalized.version_content ||
    {};
  const text = [
    row?.action,
    row?.comment,
    mergedFlat?.action_to_show,
  ]
    .filter((x) => typeof x === "string" && x.trim())
    .join("\n");
  if (!text) return false;
  const re = new RegExp(
    `\\b(?:Save|Edit|Update|Add|Delete|Create|Remove|Move|Copy)\\s+${frag}\\s+${n}\\b`,
    "i",
  );
  return re.test(text);
}

/**
 * VersionHistoryPopup - Reusable version history popup component
 *
 * Features:
 * - Sortable columns (Date, Version ID, Activity, Modified by, Comment)
 * - Filtering (All Versions, Node History)
 * - CSV export functionality
 * - Responsive design
 * - EST timezone formatting
 * - Profile image display
 *
 * @param {Object} props
 * @param {function} props.onClose - Function to close popup
 * @param {Array} props.versionHistories - Array of version history data
 * @param {string} props.mode - "add" or "edit" mode
 * @param {string} props.versionId - Current version row id; for Node History, also walks `parent_version` over loaded rows so copy chains (new `preview_tree_node_id` each revision) stay visible
 * @param {string} props.nodeId - Node ID for Node History filtering (e.g., element_id, step_tree_node_id, etc.)
 * @param {string} props.nodeIdField - Field name in version_content to match nodeId (e.g., "element_id", "step_tree_node_id")
 * @param {string} [props.breadcrumbText] - Part + Rev fallback for sparse rows (e.g. "13310 > Rev D")
 * @param {Array} [props.revisionTreeNodes] - Full procedure tree for Section/Step path in Version ID column
 * @param {string} [props.procedureRevisionIdForNodeHistory] - When Node History is selected with no nodeId, optional scope to this procedure revision
 * @param {boolean} [props.restrictNodeHistoryToProcedureRevision] - If true, apply procedure filter for Node History when nodeId is absent (e.g. sparse Redux data)
 * @param {boolean} [props.viewActionDisabled] - Disable the preview / compare arrow (e.g. no view permission)
 * @param {function} [props.isRowViewActionDisabled] - Per-row: return true to disable the arrow (e.g. no audit_trail_id)
 * @param {boolean} [props.exportCsvDisabled] - Disable the Export CSV button
 * @param {string} [props.exportCsvButtonTitle] - `title` on Export when disabled
 * @param {boolean} [props.comparisonSelect] - When true, default title and blue header ({@link VERSION_HISTORY_COMPARISON_SELECT_TITLE}) unless `title` / `headerStyle` override
 * @param {string} [props.title] - Popup title (defaults: "Version History", or comparison title if comparisonSelect)
 * @param {string} [props.procedurePartNumber] - Part number / procedure label from URL (e.g. route `partnumber`) for title "Version History for … Rev …"
 * @param {string} [props.procedureRevision] - Revision letter/name from URL (e.g. route `revision`) paired with procedurePartNumber
 * @param {function} props.onExportCSV - Custom CSV export handler (optional)
 * @param {function} props.onViewDetails - Handler for view details action (optional)
 * @param {number|null} [props.nodeHistoryStepOrdinal] - Global step number (1-based across procedure) for Node History text fallback on view-step / view-substep.
 * @param {string|null} [props.nodeHistorySubstepLetterSuffix] - Alphanumeric substep suffix (e.g. "A", "AA") paired with nodeHistoryStepOrdinal for Substep N{A} audit lines on view-substep.
 * @param {number|null} [props.nodeHistoryLeafMediaOrdinal] - Global media index for sparse audit lines on view-childnode (e.g. Attachment 3).
 * @param {string|null} [props.nodeHistoryLeafMediaPhraseRegex] - Regex fragment for label (e.g. "Attachment", "Rich(?:\\\\s+Text)?").
 * @param {boolean} [props.historyFilterDropdownDisabled] - When true, lock filter to Revision History and disable the Revision / Node dropdown (Document screen).
 * @param {string} props.emptyMessage - Custom message when no data (optional)
 */
const VersionHistoryPopup = ({
  onClose,
  versionHistories = [],
  mode,
  versionId,
  nodeId,
  nodeIdField = "element_id",
  breadcrumbText,
  revisionTreeNodes,
  procedureRevisionIdForNodeHistory,
  restrictNodeHistoryToProcedureRevision = false,
  viewActionDisabled = false,
  isRowViewActionDisabled,
  exportCsvDisabled = false,
  exportCsvButtonTitle,
  title,
  comparisonSelect = false,
  onExportCSV,
  onViewDetails,
  emptyMessage = "No version history available",
  initialFilterType = "all",
  historyFilterDropdownDisabled = false,
  /** Part label from route (e.g. `/edit-step/:partnumber/:revision/...`) — used when `title` is omitted. */
  procedurePartNumber,
  /** Revision label from route — paired with {@link procedurePartNumber} for the popup title. */
  procedureRevision,
  /** Additional UUIDs for Node History matching (merged with nodeId). */
  nodeHistoryMatchIds = null,
  nodeHistorySectionOrdinal = null,
  nodeHistoryStepOrdinal = null,
  nodeHistorySubstepLetterSuffix = null,
  nodeHistoryLeafMediaOrdinal = null,
  nodeHistoryLeafMediaPhraseRegex = null,
  currentVersionId = null,
  headerStyle,
}) => {
  const resolvedTitle =
    title != null && String(title).trim() !== ""
      ? title
      : comparisonSelect
        ? VERSION_HISTORY_COMPARISON_SELECT_TITLE
        : (() => {
            const pn =
              procedurePartNumber != null
                ? String(procedurePartNumber).trim()
                : "";
            const rev =
              procedureRevision != null
                ? String(procedureRevision).trim()
                : "";
            if (pn && rev) return `Version History for ${pn} Rev ${rev}`;
            if (pn) return `Version History for ${pn}`;
            return "Version History";
          })();

  const resolvedHeaderStyle = useMemo(() => {
    if (!comparisonSelect) return headerStyle;
    return {
      ...VERSION_HISTORY_COMPARISON_HEADER_STYLE,
      ...(headerStyle && typeof headerStyle === "object" ? headerStyle : {}),
    };
  }, [comparisonSelect, headerStyle]);

  // Local state for popup functionality
  const [filterType, setFilterType] = useState(initialFilterType);
  const revisionHistoryOnlyLocked = historyFilterDropdownDisabled === true;
  /** Document screen: force Revision History (`all`) when dropdown is disabled. */
  const effectiveFilterType = revisionHistoryOnlyLocked ? "all" : filterType;
  const [clientSearchQuery, setClientSearchQuery] = useState("");
  /** Parsed from AppTable `search_type` when non-empty search is active; null when cleared / unspecified. */
  const [clientSearchFields, setClientSearchFields] = useState(null);
  /** Synced from AppTable header clicks: { key, direction: 'ASC'|'DESC'|'DEF'|null } */
  const [tableSortConfig, setTableSortConfig] = useState({
    key: null,
    direction: null,
  });

  // Filter dropdown content
  const contentBeforeSearchFilter = (
    <FilterDropdown
      filterType={effectiveFilterType}
      setFilterType={setFilterType}
      disableFilter={revisionHistoryOnlyLocked}
      greyChevronDropdownIcon
      filterOptions={[
        { value: "all", label: "Revision History" },
        { value: "versions_for_this_page", label: "Node History" },
      ]}
    />
  );

  // Get app setup state for timezone
  const appSetupState = useSelector((state) => state.appSetup);

  // Format date to app's configured timezone
  const formatDateToEST = (dateString) => {
    const { formatDateToAppTimezone } = require("../../util/timezoneUtils");
    return formatDateToAppTimezone(dateString, appSetupState);
  };

  // Filter version histories based on filter type (then client search)
  const filteredByType = useMemo(() => {
    const anchorLineageSet =
      effectiveFilterType === "versions_for_this_page"
        ? computeVersionAncestorIdSet(
            normalizeVersionIdRef(versionId),
            versionHistories,
          )
        : null;

    return versionHistories.filter((row) => {
      if (effectiveFilterType === "all") return true;
      if (effectiveFilterType === "versions_for_this_page") {
        const combinedRawIds = [
          ...(nodeId ? [nodeId] : []),
          ...(Array.isArray(nodeHistoryMatchIds) ? nodeHistoryMatchIds : []),
        ];
        const matchIdSet = normalizeNodeHistoryMatchIdSet(combinedRawIds);
        const inVersionLineage =
          anchorLineageSet != null &&
          row.version_id != null &&
          anchorLineageSet.has(String(row.version_id).trim());
        if (matchIdSet.size > 0) {
          const subSuffix =
            typeof nodeHistorySubstepLetterSuffix === "string"
              ? nodeHistorySubstepLetterSuffix.trim()
              : "";
          const stepOrdNum = Number(nodeHistoryStepOrdinal);
          const useSubstepOrdinalFallback =
            subSuffix !== "" &&
            Number.isFinite(stepOrdNum) &&
            stepOrdNum >= 1;
          const leafOrdNum = Number(nodeHistoryLeafMediaOrdinal);
          const leafPhrase =
            typeof nodeHistoryLeafMediaPhraseRegex === "string"
              ? nodeHistoryLeafMediaPhraseRegex.trim()
              : "";
          const useLeafMediaFallback =
            leafPhrase !== "" &&
            Number.isFinite(leafOrdNum) &&
            leafOrdNum >= 1;
          return (
            inVersionLineage ||
            rowMatchesNodeHistoryVersionContent(
              row,
              matchIdSet,
              nodeIdField,
            ) ||
            rowMatchesSectionAuditOrdinal(row, nodeHistorySectionOrdinal) ||
            (useSubstepOrdinalFallback
              ? rowMatchesSubstepAuditOrdinal(
                  row,
                  nodeHistoryStepOrdinal,
                  subSuffix,
                )
              : rowMatchesStepAuditOrdinal(row, nodeHistoryStepOrdinal)) ||
            (useLeafMediaFallback
              ? rowMatchesLeafMediaAuditOrdinal(
                  row,
                  nodeHistoryLeafMediaOrdinal,
                  leafPhrase,
                )
              : false)
          );
        }
        if (inVersionLineage) return true;
        if (
          restrictNodeHistoryToProcedureRevision &&
          procedureRevisionIdForNodeHistory != null &&
          String(procedureRevisionIdForNodeHistory).trim() !== ""
        ) {
          const id = String(procedureRevisionIdForNodeHistory);
          const pr = row?.version_content?.procedure_revision_id;
          const prp =
            row?.parent_version_data?.version_content?.procedure_revision_id;
          if (pr == null && prp == null) return false;
          return String(pr ?? "") === id || String(prp ?? "") === id;
        }
        return true;
      }
      return versionId === row.version_id;
    });
  }, [
    versionHistories,
    effectiveFilterType,
    nodeId,
    nodeIdField,
    versionId,
    nodeHistoryMatchIds,
    nodeHistorySectionOrdinal,
    nodeHistoryStepOrdinal,
    nodeHistorySubstepLetterSuffix,
    nodeHistoryLeafMediaOrdinal,
    nodeHistoryLeafMediaPhraseRegex,
    procedureRevisionIdForNodeHistory,
    restrictNodeHistoryToProcedureRevision,
  ]);

  const filteredVersionHistories = useMemo(() => {
    const q = (clientSearchQuery || "").trim().toLowerCase();
    if (!q) return filteredByType;

    const fields =
      Array.isArray(clientSearchFields) && clientSearchFields.length > 0
        ? clientSearchFields
        : VERSION_HISTORY_FILTER_FIELDS;

    const ctx = {
      breadcrumbText,
      revisionTreeNodes,
      formatDate: formatDateToEST,
    };

    const fullCover =
      fields.length === VERSION_HISTORY_FILTER_FIELDS.length &&
      VERSION_HISTORY_FILTER_FIELDS.every((f) => fields.includes(f));

    return filteredByType.filter((row) => {
      let hay = fields
        .map((f) => versionHistoryRowSearchHaystack(row, f, ctx))
        .join(" ")
        .toLowerCase();
      if (fullCover) {
        hay += ` ${versionHistoryFullSearchExtras(row).toLowerCase()}`;
      }
      return hay.includes(q);
    });
  }, [
    filteredByType,
    clientSearchQuery,
    clientSearchFields,
    breadcrumbText,
    revisionTreeNodes,
    appSetupState,
  ]);

  const handleTableFilterSort = useCallback((queryString) => {
    const { search, fields } = parseTableSearchPayload(queryString);
    setClientSearchQuery(search);
    setClientSearchFields(
      fields && fields.length > 0 ? fields : null,
    );
  }, []);

  const displayVersionHistories = sortVersionHistoryNewestFirst(
    filteredVersionHistories,
  );

  const rowsSortedForTable = useMemo(
    () =>
      sortVersionHistoryRows(
        displayVersionHistories,
        tableSortConfig.key,
        tableSortConfig.direction,
        breadcrumbText,
        revisionTreeNodes,
      ),
    [
      displayVersionHistories,
      tableSortConfig.key,
      tableSortConfig.direction,
      breadcrumbText,
      revisionTreeNodes,
    ],
  );

  const handleDefaultExportCSV = () => {
    if (mode === "add") {
      return; // No version history for add mode
    }

    const header = ["Date", "Version ID", "Activity", "Modified by", "Comment"];
    const rows = rowsSortedForTable.map((row) => {
      const { formatDateToAppTimezone } = require("../../util/timezoneUtils");
      const finalString = formatDateToAppTimezone(row.audit_date, appSetupState);

      const versionIdWithPath = formatVersionHistoryIdDisplay(
        row,
        breadcrumbText,
        revisionTreeNodes,
      );

      return [
        finalString,
        versionIdWithPath,
        row.action || "-",
        `${row.first_name || ""} ${row.last_name || ""}`.trim() || "-",
        row.comment || "-",
      ];
    });

    const csv = [header, ...rows]
      .map((r) => r.map((v) => `"${v}"`).join(","))
      .join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "version_history.csv";
    a.click();
    window.URL.revokeObjectURL(url);
  };

  return (
    <Popup
      title={resolvedTitle}
      closeModal={onClose}
      headerStyle={resolvedHeaderStyle}
    >
      <div
        style={{
          minWidth: 900,
          maxWidth: 1480,
          minHeight: 400,
          paddingLeft: 16,
          paddingRight: 16,
        }}
      >
        <div className="version-history-popup-app-table">
          <AppTable
            setLocalSortConfig={setTableSortConfig}
            onFilterSortHandler={handleTableFilterSort}
            showScroll
            pagination={VERSION_HISTORY_POPUP_APP_TABLE_PAGINATION}
            columnsData={VERSION_HISTORY_POPUP_COLUMNS_DATA}
            showSearchInput={true}
            contentBeforeSearchFilter={contentBeforeSearchFilter}
            usingInPopup={true}
            extrapadding="0px"
          >
            {mode === "add" ? (
              <tr>
                <td
                  colSpan="6"
                  style={{ textAlign: "center", padding: "20px" }}
                >
                  {emptyMessage}
                </td>
              </tr>
            ) : (
              rowsSortedForTable.map((row, idx) => {
                const isCurrentVersionRow =
                  currentVersionId != null &&
                  currentVersionId !== "" &&
                  row.version_id != null &&
                  String(row.version_id) === String(currentVersionId);
                const rowActionBlocked =
                  typeof isRowViewActionDisabled === "function" &&
                  isRowViewActionDisabled(row) === true;
                const actionDisabled =
                  isCurrentVersionRow || viewActionDisabled || rowActionBlocked;
                const rowKey =
                  row.audit_trail_id_ref ??
                  row.audit_trail_id ??
                  row.id ??
                  row.version_id ??
                  idx;
                return (
                <tr key={String(rowKey)} style={isCurrentVersionRow ? { color: "#0D6EFD" } : {}}>
                  <td style={{ textAlign: "left" }}>
                    {formatDateToEST(row.audit_date)}
                  </td>
                  <td
                    style={{
                      whiteSpace: "normal",
                      wordBreak: "break-word",
                      verticalAlign: "top",
                    }}
                  >
                    {formatVersionHistoryIdDisplay(
                      row,
                      breadcrumbText,
                      revisionTreeNodes,
                    )}
                  </td>
                  <td>{row.action || ""}</td>
                  <td>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                      }}
                    >
                      <img
                        src={Images.Profile}
                        alt="Profile"
                        style={{
                          width: "20px",
                          height: "20px",
                          borderRadius: "50%",
                          objectFit: "cover",
                        }}
                      />
                      <span>
                        {row.first_name || ""} {row.last_name || ""}
                      </span>
                    </div>
                  </td>
                  <td>
                    {isCurrentVersionRow
                      ? `${row.comment || "-"} (Current Selection)`
                      : row.comment || "-"}
                  </td>
                  <td>
                    <button
                      type="button"
                      className="right-arrow"
                      disabled={actionDisabled}
                      title={
                        isCurrentVersionRow
                          ? "Already the version you are comparing from"
                          : viewActionDisabled
                            ? "You do not have permission to view or edit documents."
                            : rowActionBlocked
                              ? "No preview available for this version"
                              : "View version preview"
                      }
                      aria-label={
                        actionDisabled
                          ? "Forward disabled"
                          : "Compare with this version"
                      }
                      style={{
                        pointerEvents: actionDisabled ? "none" : "auto",
                        opacity: actionDisabled ? 0.35 : 1,
                        cursor: actionDisabled ? "not-allowed" : "pointer",
                      }}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        if (actionDisabled) return;
                        if (onViewDetails) {
                          onViewDetails(row);
                        }
                      }}
                    >
                      <FontAwesomeIcon icon={faArrowRight} />
                    </button>
                  </td>
                </tr>
                );
              })
            )}
          </AppTable>
        </div>

        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            marginTop: 16,
            marginBottom: 16,
            marginRight: 16,
          }}
        >
          <ExportCsvBtn
            handleExportCSV={onExportCSV || handleDefaultExportCSV}
            disabled={exportCsvDisabled}
            title={exportCsvButtonTitle}
          />
        </div>
      </div>
    </Popup>
  );
};

export default VersionHistoryPopup;
